import React from 'react';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  MenuDivider,
  Portal,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  ArrowRightLeft,
  CirclePause,
  ClipboardCheck,
  Ellipsis,
  Info,
  IterationCcw,
  PlayCircle,
  PlusSquare,
  RouteOff,
  ScrollText,
  Trash2,
} from 'lucide-react';
import { FiEdit2, FiMoreHorizontal } from 'react-icons/fi/index.js';
import {
  StudentCourseEnrolmentStatus,
  StudentCourseEnrolmentStatusSchema,
} from '@duke-hq/svc-api-db/schemas/enums/StudentCourseEnrolmentStatus.schema';
import {
  CourseStatus,
  CourseStatusSchema,
} from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { CourseSegment } from '@duke-hq/shared-constants/enrolments';

interface EnrolmentActionsMenuProps {
  enrolment: {
    id: number;
    status: StudentCourseEnrolmentStatus;
    course_segment: string;
    course?: {
      status?: CourseStatus;
    } | null;
  };
  onViewEnrolment?: () => void;
  onUpdateEnrolment: () => void;
  onWithdrawEnrolment: () => void;
  onRestoreEnrolment: () => void;
  onPauseResumeEnrolment: () => void;
  onViewDocuments: () => void;
  onAddSemester?: () => void;
  onTransferToSupersedingCourse?: () => void;
  onDeleteEnrolment?: () => void;
  onMarkAllWpComplete?: () => void;
  variant?: 'table' | 'header';
  stopPropagation?: boolean;
}

export function EnrolmentActionsMenu({
  enrolment,
  onViewEnrolment,
  onUpdateEnrolment,
  onWithdrawEnrolment,
  onRestoreEnrolment,
  onPauseResumeEnrolment,
  onViewDocuments,
  onAddSemester,
  onTransferToSupersedingCourse,
  onDeleteEnrolment,
  onMarkAllWpComplete,
  variant = 'header',
  stopPropagation = false,
}: EnrolmentActionsMenuProps): React.ReactElement {
  const handleClick = (callback: () => void) => (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    callback();
  };

  const showRestore =
    enrolment.status === StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN &&
    enrolment.course != null &&
    enrolment.course.status !== CourseStatusSchema.enum.SUPERSEDED;

  // A completed enrolment is final and cannot be withdrawn — except a completed
  // first-semester-only enrolment, which can still be withdrawn so it can be transferred
  // onto a superseding course.
  const isCompletedNonFirstSemesterOnly =
    enrolment.status === StudentCourseEnrolmentStatusSchema.enum.COMPLETED &&
    enrolment.course_segment !== CourseSegment.FIRST_SEMESTER_ONLY;
  const showWithdraw =
    enrolment.status !== StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN &&
    !isCompletedNonFirstSemesterOnly;

  const isPaused = enrolment.status === StudentCourseEnrolmentStatusSchema.enum.PAUSED;
  const pauseResumeDisabled =
    enrolment.status === StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN ||
    enrolment.status === StudentCourseEnrolmentStatusSchema.enum.PENDING ||
    enrolment.status === StudentCourseEnrolmentStatusSchema.enum.COMPLETED;

  const MenuWrapper = variant === 'table' ? Portal : React.Fragment;

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        aria-label="Options"
        icon={variant === 'table' ? <Ellipsis width="20px" height="20px" /> : <FiMoreHorizontal />}
        variant="ghost"
        size={variant === 'table' ? 'sm' : undefined}
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        display={variant === 'table' ? 'inline-flex' : undefined}
      />
      <MenuWrapper>
        <MenuList>
          {onAddSemester ? (
            <MenuItem icon={<PlusSquare size={16} />} onClick={handleClick(onAddSemester)}>
              <Trans>Add semester</Trans>
            </MenuItem>
          ) : null}

          {onViewEnrolment ? (
            <MenuItem icon={<Info width={16} height={16} />} onClick={handleClick(onViewEnrolment)}>
              <Trans>View enrolment</Trans>
            </MenuItem>
          ) : null}

          <MenuItem icon={<FiEdit2 />} onClick={handleClick(onUpdateEnrolment)}>
            <Trans>Update enrolment</Trans>
          </MenuItem>

          {showWithdraw ? (
            <MenuItem
              icon={<RouteOff width={16} height={16} />}
              onClick={handleClick(onWithdrawEnrolment)}
            >
              <Trans>Withdraw enrolment</Trans>
            </MenuItem>
          ) : showRestore ? (
            <MenuItem
              icon={<IterationCcw width={16} height={16} />}
              onClick={handleClick(onRestoreEnrolment)}
            >
              <Trans>Restore enrolment</Trans>
            </MenuItem>
          ) : null}

          {onTransferToSupersedingCourse ? (
            <MenuItem
              icon={<ArrowRightLeft size={16} />}
              onClick={handleClick(onTransferToSupersedingCourse)}
            >
              <Trans>Transfer to superseding course</Trans>
            </MenuItem>
          ) : null}

          <MenuItem
            icon={isPaused ? <PlayCircle size={16} /> : <CirclePause size={16} />}
            isDisabled={pauseResumeDisabled}
            onClick={handleClick(onPauseResumeEnrolment)}
          >
            {isPaused ? t`Resume enrolment` : t`Pause enrolment`}
          </MenuItem>

          <MenuItem icon={<ScrollText size={16} />} onClick={handleClick(onViewDocuments)}>
            <Trans>Documents</Trans>
          </MenuItem>

          {onMarkAllWpComplete ? (
            <MenuItem
              icon={<ClipboardCheck size={16} />}
              onClick={handleClick(onMarkAllWpComplete)}
            >
              <Trans>Mark all work placements complete</Trans>
            </MenuItem>
          ) : null}

          {onDeleteEnrolment ? (
            <>
              <MenuDivider />
              <MenuItem
                icon={<Trash2 width={16} height={16} />}
                color="red.700"
                onClick={handleClick(onDeleteEnrolment)}
                _hover={{
                  color: 'bg.surface',
                  background: 'red.700',
                }}
              >
                <Trans>Delete enrolment</Trans>
              </MenuItem>
            </>
          ) : null}
        </MenuList>
      </MenuWrapper>
    </Menu>
  );
}
